import { formatISO, formatISO9075 } from 'date-fns';

import { accountAPI, accountQuery } from '@/api/account';
import { activityAPI, activityQuery } from '@/api/activity';
import { classAPI, classQuery } from '@/api/class';
import { contractAPI, contractQuery } from '@/api/contract';
import { dashboardAPI, dashboardQuery } from '@/api/dashboard';
import { dataAPI, dataQuery } from '@/api/data';
import { employeeAPI, employeeQuery } from '@/api/employee';
import { exportAPI } from '@/api/export';
import { invoiceAPI, invoiceQuery } from '@/api/invoice';
import { kpiApi, kpiQuery } from '@/api/kpi';
import { leadAPI, leadQuery } from '@/api/lead';
import { masterDataAPI, masterDataQuery } from '@/api/master-data';
import { permissionAPI, permissionQuery } from '@/api/permission';
import { pendingRequestAPI, pendingRequestQuery } from '@/api/request-approval';
import { roleAPI, roleQuery } from '@/api/role';
import { scheduleAPI, scheduleQuery } from '@/api/schedule';
import { studentAPI, studentQuery } from '@/api/student';
import { userAPI, userQuery } from '@/api/user';

export const api = {
  ...masterDataAPI,
  lead: leadAPI,
  contract: contractAPI,
  account: accountAPI,
  invoice: invoiceAPI,
  activity: activityAPI,
  employee: employeeAPI,
  class: classAPI,
  user: userAPI,
  student: studentAPI,
  schedule: scheduleAPI,
  pending_request: pendingRequestAPI,
  role: roleAPI,
  kpi: kpiApi,
  permission: permissionAPI,
  dashboard: dashboardAPI,
  data: dataAPI,
  export: exportAPI,
};

export const query = {
  lead: leadQuery,
  contract: contractQuery,
  account: accountQuery,
  invoice: invoiceQuery,
  activity: activityQuery,
  employee: employeeQuery,
  master_data: masterDataQuery,
  class: classQuery,
  user: userQuery,
  student: studentQuery,
  schedule: scheduleQuery,
  pending_request: pendingRequestQuery,
  role: roleQuery,
  kpi: kpiQuery,
  permission: permissionQuery,
  dashboard: dashboardQuery,
  data: dataQuery,
} as const;

export const getQueryParameters = (queryParams: QueryParams<Partial<SearchFormData>>) => {
  let query: Record<string, string | number | Date | Array<string | number> | unknown> = {};
  if (!queryParams) return query;

  const params = { ...queryParams.params };
  const pagination = { ...queryParams.pagination };

  query = {
    ...params,
    ...pagination,
    start_date: params.start_date ? formatISO(params.start_date) : '',
    end_date: params.end_date ? formatISO(params.end_date) : '',
    date_from: params.date_from ? formatISO9075(params.date_from) : '',
    date_to: params.date_to ? formatISO9075(params.date_to) : '',
    current_date: params.current_date ? formatISO9075(params.current_date) : '',
    is_active: true,
    [params.search_keyword_type || 'all']: typeof params.keyword === 'string' ? params.keyword.trim() : params.keyword,
  };

  if ('class_status' in query) {
    query['class_status_list'] = (query['class_status'] as string[]).join(',');
    delete query.class_status;
  }

  if ('statuses' in query) {
    query['status_list'] = query['statuses'] as string;
    delete query.statuses;
  }

  if ('levels' in query) {
    query['level_list'] = query['levels'] as string;
    delete query.levels;
  }

  if ('class_name' in query) {
    query['class_code'] = query['class_name'] as string;
    delete query.class_name;
  }

  if ('lead_source' in query) {
    const id = (query['lead_source'] as MasterDataType).id;
    query['lead_source_id_list'] = id !== -1 ? id : '';
    delete query.lead_source;
  }

  if ('product' in query) {
    const id = (query['product'] as MasterDataType).id;
    query['product_id'] = id !== -1 ? id : '';
    delete query.product;
  }

  if ('products' in query) {
    const ids = (query['products'] as MasterDataType[]).map((item) => item.id);
    query['product_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.products;
  }

  if ('class_room' in query) {
    const id = (query['class_room'] as MasterDataType).id;
    query['class_room_id_list'] = id !== -1 ? id : '';
    delete query.class_room;
  }

  if ('class_rooms' in query) {
    const ids = (query['class_rooms'] as MasterDataType[]).map((item) => item.id);
    query['class_room_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.class_rooms;
  }

  if ('work_office' in query) {
    const id = (query['work_office'] as MasterDataType).id;
    query['work_office_id'] = id !== -1 ? id : '';
    delete query.work_office;
  }

  if ('work_offices' in query) {
    const ids = (query['work_offices'] as MasterDataType[]).map((item) => item.id);
    query['work_office_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.work_offices;
  }

  if ('shift' in query) {
    const id = (query['shift'] as MasterDataType).id;
    query['shift_id_list'] = id !== -1 ? id : '';
    delete query.shift;
  }

  if ('sale_agency' in query) {
    const id = (query['sale_agency'] as MasterDataType).id;
    query['sale_agency_id'] = id !== -1 ? id : '';
    delete query.sale_agency;
  }

  if ('sale_agencies' in query) {
    const ids = (query['sale_agencies'] as MasterDataType[]).map((item) => item.id);
    query['sale_agency_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.sale_agencies;
  }

  if ('created_month_year' in query) {
    const createdMonthYear = new Date(query['created_month_year'] as string | Date);
    query['month'] = createdMonthYear.getMonth() + 1;
    query['year'] = createdMonthYear.getFullYear();
    delete query.created_month_year;
  }

  if ('department' in query) {
    const id = (query['department'] as MasterDataType).id;
    query['department_id'] = id !== -1 ? +id : '';
    delete query.department;
  }

  if ('province' in query) {
    const id = (query['province'] as MasterDataType).id;
    query['province_id'] = id !== -1 ? +id : '';
    delete query.province;
  }

  if ('employee' in query) {
    const employee = query['employee'] as Employee;
    query['by_employee_name'] = employee.id !== -1 ? employee.name : '';
    delete query.employee;
  }

  if ('role' in query) {
    const role = query['role'] as Roles;
    query['role_id'] = role.role_id !== -1 ? role.role_id : '';
    delete query.role;
  }

  if ('classes' in query) {
    const ids = (query['classes'] as Class[]).map((item) => item.id);
    query['class_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.classes;
  }

  if ('employees' in query) {
    const ids = (query['employees'] as Employee[]).map((item) => item.id);
    query['employee_id_list'] = ids.filter((id) => id !== -1) ? ids.filter((id) => id !== -1).join(',') : '';
    delete query.employees;
  }

  delete query.keyword;
  delete query.search_keyword_type;
  delete query.timestamp;

  return Object.keys(query)
    .filter((key) => query[key] && query[key] !== 'all')
    .reduce((res, key) => Object.assign(res, { [key]: query[key] }), {});
};

export const convertObjToQueryParams = (obj: Record<string, string>): string => {
  return new URLSearchParams(obj).toString();
};
